/* Repository path: frontend/src/features/privacy/PrivacyPage.tsx */

import { useEffect, useState } from "react";
import { fetchSupportLink } from "../../api/support";
import { Button } from "../../components/Button";

export function PrivacyPage({ onBack }: { onBack: () => void }) {
  // W4-09: named only when a support link is actually active, so this
  // page never states a provider the product isn't currently using. The
  // fetch is lazy and never blocks the page's own render — see
  // docs/architecture/features/W4-09.md and
  // docs/architecture/adr/ADR-0013-external-support-link-boundary.md.
  const [supportProviderLabel, setSupportProviderLabel] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    fetchSupportLink(controller.signal).then((result) => {
      if (active && result.enabled) setSupportProviderLabel(result.provider_label);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  return (
    <article className="page page--narrow prose-page">
      <p className="eyebrow">Privacy details</p>
      <h1>A small amount of data, for a narrow purpose</h1>
      <p className="lede">You can preview an estimate without giving us your name or email address. Questionnaire answers stay in the active journey unless you choose to save the plan.</p>
      <h2>When you save a plan</h2>
      <p>We use your email only to send the secure plan link and the reminder you explicitly choose. Reminder consent starts unchecked and can be turned off from the saved-plan page.</p>
      <h2>Secure access</h2>
      <p>The email link carries a short-lived token in the browser fragment. The app removes it from the address immediately and exchanges it for plan-scoped access. Email addresses, profile answers, and estimate amounts are not placed in the link.</p>
      <h2>Your controls</h2>
      <p>You can disable future reminders without deleting the estimate, or request deletion of the saved profile, contact, plan access, and future reminder. Deletion requires a separate confirmation.</p>
      <h2>What this product does not do</h2>
      <p>It does not connect to a bank, submit taxes, keep an internal calendar, or make automatic legal or benefit-eligibility decisions.</p>
      <h2>Supporting this product</h2>
      <p>
        Supporting the project is optional and happens entirely on{" "}
        {supportProviderLabel ? <strong>{supportProviderLabel}</strong> : "the payment provider"}
        ’s own website, linked from the plan after it has been sent or saved. We don’t process,
        store or see any payment details, we receive no payment notification, and we don’t record
        whether you followed the link. Supporting changes nothing about your estimate, your saved
        plan, your reminders or your privacy choices.
      </p>
      <Button variant="secondary" onClick={onBack}>Back</Button>
    </article>
  );
}
